import { useEffect } from "react"
import * as Sentry from "@sentry/react"
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom"
import { getMetaVarMaybe } from "~/common/get-meta-var"

// The app never cancels a request it still needs. Apollo cancels one only when
// nothing is listening any more, as when someone leaves a page mid load, and a
// browser extension that wraps fetch can let that cancel escape as an
// unhandled rejection. Nobody sees it (WORKWEEK-ADS-CLIENT-3), so drop it. A
// handled AbortError, or any other unhandled rejection, still reports.
export const isUnhandledAbortRejection = (
  event: Pick<Sentry.Event, "exception">
): boolean =>
  event.exception?.values?.some(
    (exception) =>
      exception.type === "AbortError" &&
      exception.mechanism?.type === "onunhandledrejection"
  ) ?? false

const environment = getMetaVarMaybe("SENTRY_ENVIRONMENT") ?? undefined
const dsn = getMetaVarMaybe("SENTRY_DSN")

if (dsn) {
  Sentry.init({
    dsn,
    environment,
    integrations: [
      // See docs for support of different versions of variation of react router
      // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
    ],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for tracing.
    tracesSampleRate: 0,

    // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
    tracePropagationTargets: [/^\/graphql\//],

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    ignoreErrors: [
      // Microsoft's email link scanners (Outlook Safe Links, Defender) open
      // links from emails in a headless browser that rejects promises with this
      // message. No person sees it and our code is not involved (ADP-1456).
      /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
    ],

    beforeSend: (event) => (isUnhandledAbortRejection(event) ? null : event),
  })
} else {
  // eslint-disable-next-line no-console
  console.warn("No Sentry DSN found, skipping Sentry initialization")
}
